<script lang="ts" module>
	export type MobileMenuClasses = {
		header?: string;
		siteLinks?: string;
		siteLink?: string;
		socials?: string;
	};

	export interface MobileMenuProps {
		showGlobalNav?: boolean | undefined;
		onClickSearch?: (() => void) | undefined;
		isSearchEnabled?: boolean | undefined;
		dark?: boolean | undefined;
		/** Show the "Menu" text next to the hamburger. Off by default so
		 * sites keep the icon-only trigger; the HENI redesign opts in. */
		showMenuLabel?: boolean | undefined;
		siteLinks: SiteNavLink[];
		globalLinks: GlobalNavLink[];
		pathname: string;
		classes?: MobileMenuClasses;
		class?: string;
		leftLink?: import('svelte').Snippet;
		rightLink?: import('svelte').Snippet;
		homeIcon?: import('svelte').Snippet;
		copyright?: import('svelte').Snippet;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { HamburgerIcon } from '../../../../../assets/icons/HamburgerIcon';
	import { SearchIcon } from '../../../../../assets/icons/SearchIcon';
	import { Txt } from '../../../../../components/Txt';
	import type { GlobalNavLink, SiteNavLink } from '../../../types';
	import { createNavPopover } from '../../../utils/createNavPopover';
	import { getNavBarContext } from '../../../utils/getNavBarContext/getNavBarContext';
	import { MobileMenuContent } from './MobileMenuContent';
	import { browser } from '$app/env';

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const MobileMenuContentAny = MobileMenuContent as any;

	let {
		showGlobalNav = true,
		onClickSearch = undefined,
		isSearchEnabled = false,
		dark = undefined,
		showMenuLabel = false,
		siteLinks,
		globalLinks,
		pathname,
		classes = {},
		leftLink,
		...rest
	}: MobileMenuProps = $props();

	const context = getNavBarContext();
	const popover = context?.popover || createNavPopover();
	const { open } = popover.states;

	const dataCyPrefix = `header-top-bar`;

	let className = $derived(
		twMerge(
			classNames(
				'inset-0-custom !left-auto right-[2.1rem] z-30 w-full bg-gray-900 lg:mt-2 lg:w-80 lg:rounded lg:p-6 xl:right-[calc(2.1rem+((100vw-1280px)/2))] lg:absolute lg:top-[3rem]',
				{ 'bg-white lg:bg-gray-900': !showGlobalNav }
			),
			rest.class
		)
	);

	const closeMenu = () => {
		open.set(false);
	};

	$effect(() => {
		if (!browser) {
			return;
		}

		window.addEventListener('resize', closeMenu);

		return () => {
			window.removeEventListener('resize', closeMenu);
		};
	});
</script>

{@render leftLink?.()}

{#if isSearchEnabled}
	<button
		type="button"
		data-cy={`${dataCyPrefix}-search`}
		class="mr-0.5 flex h-12 w-12 items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-gray-0 lg:hidden"
		onclick={onClickSearch}
		aria-label="Search HENI"
	>
		<SearchIcon class="stroke-[1.3]" color={dark ? 'gray-0' : 'gray-900'} />
	</button>
{/if}

<button
	type="button"
	onclick={() => {
		open.set(true);
	}}
	data-cy={`${dataCyPrefix}-mobile-menu-button`}
	aria-label="Open mobile menu"
	aria-expanded={$open}
	class={twMerge('lg:hidden flex h-12 items-center gap-1.5 py-1 outline-none', rest.class)}
>
	<!-- Visible label so the hamburger reads as the menu, not just an icon. Below
	     390px the site logo and the action icons need the space, so it is hidden. -->
	{#if showMenuLabel}
		<Txt
			component="span"
			variant="body2"
			class={classNames(
				'hidden uppercase tracking-[.14em] min-[390px]:inline',
				dark ? 'text-gray-0' : 'text-gray-900'
			)}
			data-cy={`${dataCyPrefix}-mobile-menu-label`}
		>
			Menu
		</Txt>
	{/if}
	<HamburgerIcon
		color={dark ? 'gray-0' : 'gray-900'}
		class="flex outline-none"
		data-cy={`${dataCyPrefix}-hamburger-icon`}
		title="Open navigation menu"
	/>
</button>

{#if $open}
	<div class={className}>
		<MobileMenuContentAny
			{classes}
			dataCy={dataCyPrefix}
			{siteLinks}
			{popover}
			{globalLinks}
			{pathname}
			{showGlobalNav}
		>
			{#snippet rightLink()}
				{@render rest?.rightLink?.()}
			{/snippet}

			{#snippet homeIcon()}
				{@render rest?.homeIcon?.()}
			{/snippet}

			{#snippet copyright()}
				{@render rest?.copyright?.()}
			{/snippet}
		</MobileMenuContentAny>
	</div>
{/if}

<style>
	@media (max-width: 1023px) {
		.inset-0-custom {
			position: fixed !important;
			height: 100dvh;
			overflow-y: scroll;
			top: 0 !important;
			right: 0 !important;
			bottom: 0 !important;
			left: 0 !important;
		}
	}
</style>
